import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import { esCamion, formatMoney, parseMoney } from '@abastece/shared';
import { db } from '../db/dexie.js';
import { sesionAbierta } from '../lib/caja.js';
import { emitirYVer } from '../lib/emitir.js';
import { usePuede, useSesion } from '../lib/sesion.js';
import { Campo } from '../ui/Campo.js';
import { Dato, QuienRecibe, enDolares } from '../ui/CuentasDeCaja.js';
import { useEmpiezaElDia } from '../ui/LoDelDia.js';
import { Modal } from '../ui/Modal.js';
import { PedirPin } from '../ui/PedirPin.js';
import { AvisoCajaDeOtro, useCajaDeOtro } from '../ui/RecibirCaja.js';
import { Pagina, Vacio } from '../ui/Pagina.js';
import { TarjetaTurno, type TurnoCerrado } from '../ui/FilasDeDinero.js';
import { CorregirConteo } from '../ui/CorregirConteo.js';
import { VerTodos } from '../ui/FiltrosHistorial.js';
import { Ico } from '../ui/Iconos.js';
import { useT } from '../i18n/index.js';

export function Caja() {
  const t = useT();
  const sesion = useSesion();
  /** The till open on this device: null for none, undefined while it is read. */
  const caja = useLiveQuery(async () => (await sesionAbierta()) ?? null, [], undefined);
  const cerradas = useLiveQuery(
    () => db.sesiones.filter((s) => s.estado === 'cerrada').reverse().limit(10).toArray(),
    [],
    [],
  );
  /**
   * A route's till closes with the route's settlement, which counts the goods and the cash in one
   * act; a corte on its own would leave the route impossible to settle (see `caja.cerrada`). Only
   * a route left open from before «Reparto» was taken out (2026-10-04): no new one opens.
   */
  const deRuta = useLiveQuery(
    async () =>
      caja && esCamion(caja.ubicacion)
        ? (await db.jornadas
            .where('estado')
            .equals('abierta')
            .filter((j) => j.sesionId === caja.id)
            .count()) > 0
        : false,
    [caja?.id, caja?.ubicacion],
    false,
  );
  const [moviendo, setMoviendo] = useState<'retiro' | 'ingreso' | null>(null);
  /** Somebody else's drawer on this device. */
  const cajaDeOtro = useCajaDeOtro();
  const empiezaElDia = useEmpiezaElDia();

  /*
   * Closed, the till is a way into «Empezar el día», and the shifts gone by. Opening it is a wizard
   * of its own (`EmpezarDia`): one question to a screen, for whoever is new to it (the owner,
   * 2026-10-03).
   */
  if (caja === undefined || (caja === null && empiezaElDia === undefined)) return <Pagina titulo={t('caja.titulo')}>{null}</Pagina>;
  if (caja === null) {
    return (
      <Pagina titulo={t('caja.titulo')} subtitulo={sesion?.nombre}>
        <section className="tarjeta flex flex-col gap-4">
          <h2 className="text-lg font-semibold">{t('caja.cerradaTitulo')}</h2>
          <p className="text-sm text-slate-400">{t('caja.cerradaExplicacion')}</p>
          <Link to="/caja/empezar" className="btn-primario" data-guia="caja-empezar">
            <Ico icono="caja" />
            {t(empiezaElDia ? 'turno.empezarDia' : 'turno.empezarTurno')}
          </Link>
        </section>

        {cerradas.length > 0 && <Historial sesiones={cerradas} />}
      </Pagina>
    );
  }

  return (
    <Pagina
      titulo={t('caja.abierta')}
      subtitulo={t('caja.subtitulo', { n: caja.ventas })}
      acciones={
        <Link to="/pos" className="btn-primario gap-1.5 px-4 text-sm">
          <Ico icono="vender" className="size-4" />
          {t('inicio.vender')}
        </Link>
      }
    >
      {cajaDeOtro && (
        <div className="mb-4">
          <AvisoCajaDeOtro nombre={cajaDeOtro.nombre} />
        </div>
      )}
      <section className="tarjeta mb-4 flex flex-col gap-2">
        <Dato etiqueta={t('caja.fondoInicial')} valor={formatMoney(caja.fondoInicial)} />
        {(caja.diferenciaApertura ?? 0) !== 0 && (
          <Dato
            etiqueta={t('caja.diferenciaApertura')}
            valor={formatMoney(caja.diferenciaApertura!, { signed: true })}
          />
        )}
        {(caja.fondoDolares ?? 0) > 0 && (
          <Dato etiqueta={t('caja.fondoEnDolares')} valor={enDolares(caja.fondoDolares!)} />
        )}
        <Dato etiqueta={t('caja.ventasTurno')} valor={formatMoney(caja.totalVendido)} />
        <Dato etiqueta={t('caja.efectivoRecibido')} valor={formatMoney(caja.efectivoCordobas)} />
        {caja.efectivoDolares > 0 && (
          <Dato etiqueta={t('caja.dolaresRecibidos')} valor={enDolares(caja.efectivoDolares)} />
        )}
        {(caja.retirosDolares ?? 0) > 0 && (
          <Dato etiqueta={t('caja.dolaresRetirados')} valor={`-${enDolares(caja.retirosDolares!)}`} />
        )}
        {(caja.ingresosDolares ?? 0) > 0 && (
          <Dato etiqueta={t('caja.dolaresIngresados')} valor={enDolares(caja.ingresosDolares!)} />
        )}
        {caja.otrosPagos > 0 && (
          <Dato etiqueta={t('caja.otrosMedios')} valor={formatMoney(caja.otrosPagos)} />
        )}
        {caja.cambioEntregado > 0 && (
          <Dato etiqueta={t('caja.vueltoEntregado')} valor={`-${formatMoney(caja.cambioEntregado)}`} />
        )}
        {caja.retiros > 0 && <Dato etiqueta={t('caja.retiros')} valor={`-${formatMoney(caja.retiros)}`} />}
        {caja.ingresos > 0 && <Dato etiqueta={t('caja.ingresos')} valor={formatMoney(caja.ingresos)} />}
        {/* Fiado is part of what was sold and none of what is in the drawer; abonos and refunds
            in cash move the drawer without being sales. Each said on its own line, so the corte
            adds up in front of whoever is doing it. */}
        {(caja.abonos ?? 0) > 0 && (
          <Dato etiqueta={t('caja.abonos')} valor={formatMoney(caja.abonos!)} />
        )}
        {(caja.devoluciones ?? 0) > 0 && (
          <Dato etiqueta={t('caja.devoluciones')} valor={`-${formatMoney(caja.devoluciones!)}`} />
        )}
        {(caja.pagado ?? 0) > 0 && (
          <Dato etiqueta={t('caja.pagadoDeGaveta')} valor={`-${formatMoney(caja.pagado!)}`} />
        )}
        {(caja.fiado ?? 0) > 0 && (
          <Dato etiqueta={t('caja.fiado')} valor={formatMoney(caja.fiado!)} />
        )}
        {/* A handover counted short or over: the till goes on from the count, so the lines only
            add up to what it expects with the difference among them. Left out, a till received
            C$ 30 short read C$ 30 more than it holds. */}
        {(caja.ajusteCordobas ?? 0) !== 0 && (
          <Dato etiqueta={t('caja.ajusteEntrega')} valor={formatMoney(caja.ajusteCordobas!, { signed: true })} />
        )}
        {(caja.ajusteDolares ?? 0) !== 0 && (
          <Dato etiqueta={t('caja.ajusteEntregaDolares')} valor={enDolares(caja.ajusteDolares!, true)} />
        )}
      </section>

      <section className="mb-4 grid grid-cols-2 gap-3">
        <button type="button" className="btn-secundario" onClick={() => setMoviendo('retiro')}>
          <Ico icono="retirar" />
          {t('caja.retirar')}
        </button>
        <button type="button" className="btn-secundario" onClick={() => setMoviendo('ingreso')}>
          <Ico icono="ingresar" />
          {t('caja.ingresar')}
        </button>
      </section>

      {deRuta ? (
        // «Terminar mi turno» settles it (`LiquidarRuta`), named for what it does here.
        <div className="tarjeta flex flex-col gap-3">
          <p className="text-sm text-slate-400">{t('caja.deRuta')}</p>
          <Link to="/caja/terminar" className="btn-primario w-full">
            <Ico icono="liquidar" />
            {t('inicio.liquidarRuta')}
          </Link>
        </div>
      ) : (
        // The corte is a wizard of its own (`TerminarTurno`): whoever comes next, the drawer counted
        // note by note, what stays, who takes the rest, and at the day's end what the day was.
        <Link to="/caja/terminar" className="btn-primario w-full" data-guia="caja-corte">
          <Ico icono="corte" />
          {t('turno.terminarTurno')}
        </Link>
      )}

      {/* The shifts gone by, which the closed till lists: with one open, a tap away all the same. */}
      <div className="mt-6 flex justify-center">
        <Link to="/caja/turnos" className="btn-texto gap-1.5 text-sm text-slate-400" data-ver-todos="/caja/turnos">
          <Ico icono="corte" className="size-4" />
          {t('caja.turnosAnteriores')}
        </Link>
      </div>

      {moviendo && (
        <MovimientoCaja
          tipo={moviendo}
          sesionId={caja.id}
          onCerrar={() => setMoviendo(null)}
        />
      )}

    </Pagina>
  );
}

function MovimientoCaja({
  tipo,
  sesionId,
  onCerrar,
}: {
  tipo: 'retiro' | 'ingreso';
  sesionId: string;
  onCerrar: () => void;
}) {
  const t = useT();
  const sesion = useSesion();
  const puedeRecibir = usePuede('recibir_efectivo');
  const [monto, setMonto] = useState('');
  const [motivo, setMotivo] = useState('');
  /** Dollars go out as dollars, so the corte stops expecting them rather than córdobas. */
  const [moneda, setMoneda] = useState<'NIO' | 'USD'>('NIO');
  const [recibeAhora, setRecibeAhora] = useState(false);
  const [pidiendoPin, setPidiendoPin] = useState(false);
  /** While it is being recorded: a second tap recorded the money twice (2026-10-13). */
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const valor = parseMoney(monto);
  const valido = valor !== null && valor > 0 && motivo.trim().length > 0;
  const esRetiro = tipo === 'retiro';

  async function registrar(recibidoPor?: string) {
    if (guardando) return;
    setGuardando(true);
    setError(null);
    try {
      const { rechazo } = await emitirYVer('caja.movimiento', {
        sesionId,
        tipo,
        monto: valor!,
        motivo: motivo.trim(),
        ...(moneda === 'USD' ? { moneda } : {}),
        ...(esRetiro && recibidoPor ? { recibidoPor } : {}),
      });
      // Refused — more than the drawer holds, a till closed meanwhile —: said here, nothing recorded.
      if (rechazo) setError(rechazo);
      else onCerrar();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setGuardando(false);
    }
  }

  return (
    <Modal
      titulo={t(tipo === 'retiro' ? 'caja.retirar' : 'caja.ingresar')}
      onCerrar={onCerrar}
      pie={
        <button
          type="button"
          className="btn-primario w-full"
          disabled={!valido || guardando}
          onClick={() => {
            // Whoever holds the safe takes it in hand themselves; anybody else, on their PIN.
            if (esRetiro && puedeRecibir) void registrar(sesion?.usuarioId);
            else if (esRetiro && recibeAhora) setPidiendoPin(true);
            else void registrar();
          }}
        >
          <Ico icono="guardar" />
          {t('caja.registrar')}
        </button>
      }
    >
      <div className="flex flex-col gap-4">
        <div role="radiogroup" aria-label={t('caja.moneda')} className="flex gap-1 rounded-xl bg-slate-800 p-1">
          {(['NIO', 'USD'] as const).map((m) => (
            <button
              key={m}
              type="button"
              role="radio"
              aria-checked={moneda === m}
              onClick={() => setMoneda(m)}
              data-sin-icono="pestana"
              className={`min-h-11 flex-1 rounded-lg text-sm font-medium transition ${
                moneda === m ? 'bg-marca-600 text-white' : 'text-slate-400 hover:bg-white/5 hover:text-slate-200'
              }`}
            >
              {m === 'NIO' ? 'C$' : 'US$'}
            </button>
          ))}
        </div>
        <Campo
          etiqueta={t(moneda === 'USD' ? 'caja.montoDolares' : 'caja.montoCordobas')}
          inputMode="decimal"
          className="tabular text-lg"
          value={monto}
          onChange={(e) => setMonto(e.target.value)}
          autoFocus
        />
        <Campo
          maxLength={200}
          etiqueta={t('comun.motivo')}
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          placeholder={t(tipo === 'retiro' ? 'caja.ejemploRetiro' : 'caja.ejemploIngreso')}
          autoComplete="off"
        />
        {esRetiro && <QuienRecibe puedeRecibir={puedeRecibir} ahora={recibeAhora} setAhora={setRecibeAhora} />}
      </div>
      {error && <p className="mt-4 text-sm text-rose-400">{error}</p>}
      <p className="mt-4 text-xs text-slate-500">
        {t('caja.explicacionMovimiento')}
      </p>
      {pidiendoPin && (
        <PedirPin
          motivo={t('caja.motivoRecibir')}
          onCancelar={() => setPidiendoPin(false)}
          onAutorizado={(duenoId) => {
            setPidiendoPin(false);
            void registrar(duenoId);
          }}
        />
      )}
    </Modal>
  );
}

/** The latest shifts gone by; «Ver todos» has every one, found by day, by whose and by how it squared. */
function Historial({ sesiones }: { sesiones: readonly TurnoCerrado[] }) {
  const t = useT();
  const usuarios = useLiveQuery(() => db.usuarios.toArray(), [], []);
  const nombre = (id: string) => usuarios.find((u) => u.id === id)?.nombre ?? t('ventas.alguien');
  const puedeCorregir = usePuede('configurar');
  const [corrigiendo, setCorrigiendo] = useState<{ sesionId: string; cual: 'apertura' | 'corte' } | null>(null);
  return (
    <section className="mt-6">
      <div className="mb-3 flex items-baseline justify-between gap-3">
        <h2 className="text-sm font-semibold text-slate-400">{t('caja.turnosAnteriores')}</h2>
        <VerTodos to="/caja/turnos" />
      </div>
      {sesiones.length === 0 ? (
        <Vacio mensaje={t('caja.sinTurnos')} />
      ) : (
        <ul className="flex flex-col gap-2">
          {sesiones.map((s) => (
            <TarjetaTurno
              key={s.id}
              sesion={s}
              nombre={nombre}
              {...(puedeCorregir ? { onCorregir: (cual: 'apertura' | 'corte') => setCorrigiendo({ sesionId: s.id, cual }) } : {})}
            />
          ))}
        </ul>
      )}
      {corrigiendo && <CorregirConteo {...corrigiendo} onCerrar={() => setCorrigiendo(null)} />}
    </section>
  );
}
